import { Phone, MapPin, Mail, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

interface BuyerContactInfoProps {
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  deliveryAddress?: string;
  shippingAddress?: any;
  deliveryType?: string; // 'self' = self-delivery, anything else = partner
  orderStatus?: string;
  compact?: boolean; // For table row vs modal
}

/**
 * Masks a phone number: shows first 4 and last 2 digits
 * e.g., "08012345678" → "0801****78"
 */
function maskPhone(phone: string): string {
  if (!phone || phone === 'N/A' || phone.length < 6) return phone;
  const clean = phone.replace(/\s/g, '');
  return clean.slice(0, 4) + '****' + clean.slice(-2);
}

/**
 * Masks an email: shows first 2 chars and domain
 * e.g., "john@example.com" → "jo***@example.com"
 */
function maskEmail(email: string): string {
  if (!email) return email;
  const [local, domain] = email.split('@');
  if (!domain) return email;
  return local.slice(0, 2) + '***@' + domain;
}

/**
 * Masks an address: shows only city/state
 */
function maskAddress(address: string): string {
  if (!address) return address;
  // Try to extract just city/state from end
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    return '••••••, ' + parts.slice(-2).join(', ');
  }
  return '••••••••••';
}

/**
 * Determines if full buyer details should be visible
 */
function shouldShowFullDetails(deliveryType?: string, orderStatus?: string): boolean {
  // Time-limited: hide details for completed/cancelled orders
  const completedStatuses = ['completed', 'cancelled', 'refunded'];
  if (orderStatus && completedStatuses.includes(orderStatus)) {
    return false;
  }
  
  // Self-delivery: show full details
  if (deliveryType === 'self' || deliveryType === 'self-delivery' || deliveryType === 'self_delivery') {
    return true;
  }
  
  // Partner delivery: mask details
  return false;
}

/** Copy-protection CSS class */
const noCopyClass = "select-none pointer-events-none";

/** Platform watermark for contact info */
function PlatformWatermark() {
  return (
    <span className="text-[10px] text-muted-foreground/60 italic ml-1">
      via Martketplace
    </span>
  );
}

export function BuyerContactInfoCompact({ 
  customerName, 
  customerPhone,
  deliveryType,
  orderStatus 
}: BuyerContactInfoProps) {
  const showFull = shouldShowFullDetails(deliveryType, orderStatus);
  
  return (
    <div className={noCopyClass}>
      <div className="font-medium">{customerName}</div>
      <div className="text-sm text-muted-foreground flex items-center gap-1">
        {showFull ? customerPhone : maskPhone(customerPhone)}
        {!showFull && (
          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 ml-1">
            <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
            Protected
          </Badge>
        )}
      </div>
    </div>
  );
}

export function BuyerContactInfoFull({
  customerName,
  customerPhone,
  customerEmail,
  deliveryAddress,
  shippingAddress,
  deliveryType,
  orderStatus,
}: BuyerContactInfoProps) {
  const showFull = shouldShowFullDetails(deliveryType, orderStatus);
  const isCompleted = ['completed', 'cancelled', 'refunded'].includes(orderStatus || '');
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Customer Information</h3>
        <div className="flex items-center gap-2">
          {showFull ? (
            <Badge variant="outline" className="text-xs">
              <Eye className="h-3 w-3 mr-1" />
              Full Access
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              <EyeOff className="h-3 w-3 mr-1" />
              {isCompleted ? 'Order Completed' : 'Partner Delivery'}
            </Badge>
          )}
        </div>
      </div>
      
      {isCompleted && (
        <div className="p-2 bg-muted/50 rounded-md border border-border">
          <p className="text-xs text-muted-foreground">
            🔒 Buyer details are hidden for completed orders to protect customer privacy.
          </p>
        </div>
      )}

      <div className={`grid grid-cols-2 gap-4 ${noCopyClass}`}>
        <div>
          <p className="text-sm text-muted-foreground">Name</p>
          <p className="font-medium">{customerName}<PlatformWatermark /></p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Phone</p>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <p className="font-medium">
              {showFull ? customerPhone : maskPhone(customerPhone)}
            </p>
          </div>
        </div>
        {customerEmail && (
          <div>
            <p className="text-sm text-muted-foreground">Email</p>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <p className="font-medium">
                {showFull ? customerEmail : maskEmail(customerEmail)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Delivery Address */}
      {(shippingAddress || deliveryAddress) && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Delivery Address</h4>
          <div className={`flex items-start gap-2 p-3 bg-muted/50 rounded-lg ${noCopyClass}`}>
            <MapPin className="h-4 w-4 mt-1 text-primary" />
            {showFull ? (
              <FullAddressDisplay 
                shippingAddress={shippingAddress} 
                deliveryAddress={deliveryAddress} 
              />
            ) : (
              <MaskedAddressDisplay 
                shippingAddress={shippingAddress}
                deliveryAddress={deliveryAddress}
              />
            )}
          </div>
          
          {/* Delivery Instructions - only visible for active self-delivery */}
          {showFull && (shippingAddress?.deliveryInstructions || shippingAddress?.instructions || shippingAddress?.notes) && (
            <div className="mt-2">
              <p className="text-sm font-medium text-muted-foreground mb-1">Delivery Instructions</p>
              <div className={`p-3 bg-accent/30 rounded-lg border border-accent ${noCopyClass}`}>
                <p className="text-sm">
                  {shippingAddress?.deliveryInstructions || 
                   shippingAddress?.instructions || 
                   shippingAddress?.notes}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FullAddressDisplay({ shippingAddress, deliveryAddress }: { shippingAddress?: any; deliveryAddress?: string }) {
  return (
    <div className="text-sm space-y-1">
      {shippingAddress?.fullName && (
        <p className="font-medium">{shippingAddress.fullName}<PlatformWatermark /></p>
      )}
      {shippingAddress?.phone && (
        <p className="text-muted-foreground">{shippingAddress.phone}</p>
      )}
      {shippingAddress?.addressLine1 && <p>{shippingAddress.addressLine1}</p>}
      {shippingAddress?.addressLine2 && <p>{shippingAddress.addressLine2}</p>}
      {shippingAddress?.address && !shippingAddress?.addressLine1 && (
        <p>{shippingAddress.address}</p>
      )}
      <p>
        {[shippingAddress?.city, shippingAddress?.state, shippingAddress?.postalCode]
          .filter(Boolean).join(', ')}
      </p>
      {shippingAddress?.country && shippingAddress.country !== 'Nigeria' && (
        <p>{shippingAddress.country}</p>
      )}
      {!shippingAddress?.addressLine1 && !shippingAddress?.address && deliveryAddress && (
        <p>{deliveryAddress}</p>
      )}
    </div>
  );
}

function MaskedAddressDisplay({ shippingAddress, deliveryAddress }: { shippingAddress?: any; deliveryAddress?: string }) {
  // For partner delivery, show only city/state (enough for logistics context)
  const city = shippingAddress?.city;
  const state = shippingAddress?.state;
  
  return (
    <div className="text-sm space-y-1">
      <p className="text-muted-foreground italic">
        Street address hidden — delivery handled by courier partner
      </p>
      {(city || state) && (
        <p className="font-medium">
          {[city, state].filter(Boolean).join(', ')}
        </p>
      )}
      {!city && !state && deliveryAddress && (
        <p className="text-muted-foreground">{maskAddress(deliveryAddress)}</p>
      )}
    </div>
  );
}
